import { captureException } from "./sentry";

// prep segments with extras to StateContext:
export const prepWithExtras = ({ segments, time_frame }) => {
	const segmentsArr = validateArray(segments);
	if (!(segmentsArr && segmentsArr.length)) {
		return false;
	}
	let segMap = {};
	let hitsMap = {};
	let audMap = {};
	let segmentsArray = [];
	for (let seg of segmentsArr) {
		try {
			const { code, hits, prevHits, audiences } = seg;
			segMap[code] = prepSegment(seg);
			hitsMap[code] = time_frame ? { [time_frame]: prepOnlyHits({ code, hits, prevHits }) } : {};
			audMap[code] = audiences && audiences.length ? prepFBAudiences(audiences) : [];
			let cleanSeg = { ...seg };
			delete cleanSeg.hits;
			delete cleanSeg.prevHits;
			delete cleanSeg.audiences;
			segmentsArray.push(cleanSeg);
		} catch (e) {
			console.error("dataService.js ~ prepWithExtras ~ error for segment", seg.code);
			throw e;
		}
	}
	return { segMap, hitsMap, audMap, segmentsArray };
};
export const prepSegment = (seg) => {
	const newSeg = {
		code: seg.code,
		sitename: seg.sitename,
		channel: seg.channel,
		state: seg.state,
		display: seg.display,
		install_data: seg.install_data,
		plan_id: seg.plan_id,
		last_state_change: seg.last_state_change
	};
	if (newSeg?.install_data && !newSeg.install_data?.options) {
		if (typeof newSeg.install_data === "string") {
			try {
				newSeg.install_data = eval("(" + newSeg.install_data + ")");
			} catch (e) {
				newSeg.install_data = {};
				captureException(e,
								 "info",
								  {}, 
								  { action: "prepSegment", description: `could not parse install_data of segment ${seg.code}` },
								  { scope: "dataService.js" });
			}
		}
		newSeg.install_data.options = newSeg.install_data.options || {};
	}
	return newSeg;
};
export const prepOnlyHits = ({ code, hits, prevHits }) => {
	if (code && hits && prevHits) {
		return {
			code,
			total: hits.total,
			totalChange: calcChange(hits.total, prevHits.total),
			Engaged: hits.basic,
			EngagedChange: calcChange(hits.basic, prevHits.basic),
			highEngaged: hits.high,
			mediumEngaged: hits.medium,
			highEngagedChange: calcChange(hits.high, prevHits.high)
		};
	} else return {};
};
const DAY = 1000 * 60 * 60 * 24;
/**
 *
 * @param {number} endDate - timestamp
 * @returns {number} number of remaining day to end of trial
 */
export const calcTrialDaysRemaining = (endDate) => {
	const days = Math.ceil((endDate - new Date()) / DAY);
	return days >= 0 ? days : 0;
};
export const prepPlansMap = (plans) => {
	let plansMap = {};
	const plansArray = validateArray(plans);
	if (!(plansArray && plansArray.length)) return [];
	for (let plan of plansArray) {
		plansMap[plan.plan_id] = plan;
		if (plan?.type?.toLowerCase()?.replace(" ", "_") === "trial") {
			plansMap[plan.plan_id].trial_days_remaining = calcTrialDaysRemaining(plan?.end_date);
		}
	}
	return plansMap;
};
// deep merge objects
export const mergeObjects = (...objects) => {
	// Variables
	let target = {};

	// Merge the object into the target object
	let merger = (obj) => {
		for (let prop in obj) {
			if (obj.hasOwnProperty(prop)) {
				if (Object.prototype.toString.call(obj[prop]) === "[object Object]") {
					// If we're doing a deep merge and the property is an object
					target[prop] = mergeObjects(target[prop], obj[prop]);
				} else {
					// Otherwise, do a regular merge
					target[prop] = obj[prop];
				}
			}
		}
	};

	//Loop through each object and conduct a merge
	for (let i = 0; i < objects.length; i++) {
		merger(objects[i]);
	}

	return target;
};

export const updateSegmentInData = (newSegment, prevSegments = [], prevSegMap = {}) => {
	const code = newSegment.code;
	// prep segment for fixelData
	let newSegMap = prevSegMap ? { ...prevSegMap } : {};
	let newSegments = prevSegments ? [...prevSegments] : [];
	// update segments with new segment (add or update)
	let segIndex = -1;
	if (prevSegments && prevSegments.length) {
		segIndex = prevSegments.findIndex((seg) => seg.code === code);
	}
	if (segIndex !== -1) {
		newSegments[segIndex] = newSegment;
	} else {
		newSegments.push(newSegment);
	}
	// update prepared segment in the segmentsMap
	const preparedSegment = prepSegment(newSegment);
	newSegMap[code] = prevSegMap && prevSegMap[code] ? mergeObjects(prevSegMap[code], preparedSegment) : preparedSegment;

	return { newSegments, newMap: newSegMap };
};

/**
 *
 * @param current
 * @param prev
 * @returns {null|number}
 */
const calcChange = (current, prev) => {
	const change = Math.round((100 * (current - prev)) / prev);
	return isNaN(change) ? null : change;
};

/**
 *
 * @param data
 * @returns {*}
 */
const prepPixels = (data) => {
	const arr = validateArray(data);
	if (arr) {
		return arr.map((pixel) => {
			return {
				name: `${pixel.name ? pixel.name : "Un-named"} (${pixel.id})`,
				id: pixel.id
			};
		});
	}
};

/**
 *
 * @param data
 * @returns {*}
 */
export const prepFBAccounts = (data) => {
	const arr = validateArray(data);
	if (arr) {
		return arr.map((account) => {
			return {
				name: `${account.name} (${account.account_id})`,
				id: account.id,
				pixels: account.adspixels ? prepPixels(account.adspixels.data) : []
			};
		});
	}
};

/**
 *
 * @param aud
 * @returns {string|string}
 */
const setStatus = (aud) => {
	switch (aud.status) {
		case "populating":
			return "Populating";
		case "ready":
			return aud.size >= 1000 ? "Ready" : "Populating";
		default:
			return "Inactive";
	}
};

/**
 *
 * @param fbAudiences
 * @returns {*[]|*}
 */
export const prepFBAudiences = (fbAudiences) => {
	const audArr = validateArray(fbAudiences);
	if (!(audArr && audArr.length)) return [];
	return fbAudiences.map((aud) => {
		return {
			aud_id: aud.aud_id,
			size: aud.size,
			code: aud.code,
			pixel: aud.pixel,
			pixel_name: aud.pixel_name,
			status: setStatus(aud),
			account: aud.account,
			account_name: aud.account_name,
			name: aud.name
		};
	});
};

/**
 * @param {number} timestamp
 * @returns {string}
 */
const formatDateFromTimestamp = (timestamp) => {
	if (!timestamp) return null;
	let date = new Date(timestamp);
	let day = date.getDate();
	let month = date.toLocaleString("en-us", { month: "short" });
	let year = date.getFullYear();
	return `${month} ${day}, ${year}`;
};
/**
 * @param {number} num
 * @param {number} total
 * @returns {string}
 */
const formatNumber = (num, total) => {
	if (!num) return 0;
	if (!total) {
		if (typeof parseInt(num) === "number") {
			return parseInt(num).toLocaleString("en", { useGrouping: true });
		} else {
			return 0;
		}
	} else {
		let formattedNum = num;
		if (num > 1000000) formattedNum = round(num / 1000000, 1) + "M";
		else if (num > 1000) formattedNum = round(num / 1000, 1) + "K";
		return formattedNum + " (" + round((100 * num) / total, 1) + "%)";
	}
};
/**
 * @param {number} num
 * @param {number} dig
 * @returns {number}
 */
const round = (num, dig) => {
	return Math.round(num * 10 ** dig) / 10 ** dig;
};
/**
 * @param {array} segmentsMap
 * @returns {array}
 */
export const prepSegmentsList = (segments, hitsMap, time_frame) => {
	const segArray = validateArray(segments);
	if (segArray && segArray.length && hitsMap && time_frame) {
		return segArray.map((seg) => {
			const hits = hitsMap[seg.code] && hitsMap[seg.code][time_frame];
			const segObj = {
				id: seg.code || "--",
				name: `${seg.display} (${seg.code})` || "--",
				sessions: formatNumber(hits?.total),
				basic: formatNumber(hits?.Engaged, hits?.total),
				medium: formatNumber(hits?.mediumEngaged, hits?.total),
				high: formatNumber(hits?.highEngaged, hits?.total),
				status: seg.state || "--"
			};
			return segObj;
		});
	} else return [];
};

/**
 * @param {array} plans
 * @returns {array}
 */
export const prepPlansList = (plans) => {
	const plansArray = validateArray(plans);
	if (plansArray && plansArray.length) {
		return plansArray.map((plan) => {
			const planObj = {
				id: plan.plan_id || "--",
				name: plan.type.replace("_", " ") || "--",
				price: plan.price || "--",
				start: formatDateFromTimestamp(plan.start_date) || "--",
				end: formatDateFromTimestamp(plan.end_date) || "--",
				status: plan.state || "--",
				stripe_sub_id: plan.stripe_sub_id || "--"
			};
			return planObj;
		});
	} else return [];
};
/**
 *
 * @param {any} data
 * @returns {array || null}
 */
export const validateArray = (data) => {
	if (!data) return null;
	if (!Array.isArray(data)) return [data];
	if (!data.length) return null;
	return data;
};

export const getPaymentMethod = (plan, segments, customer) => {
	const stripeChannelsArr = ["stripe", "console", "wordpress"];
	let payment_method = plan?.payment_method?.toLowerCase() || "";
	if (!payment_method && segments) {
		// this is a temp fix - need to add channel when a new plan is created on all channels (shopify/wordpress/icount/invoice)
		const seg = segments.find((s) => s.plan_id === plan?.plan_id);
		payment_method = seg?.channel?.toLowerCase();
	}
	if (!payment_method && customer) {
		// if customer has no active plan or trial and no segments
		payment_method = customer.stripe_id ? "stripe" : "";
	}
	const isStripe = stripeChannelsArr.includes(payment_method);
	return { payment_method, isStripe };
};
